import { CALENDAR_ITEM_TYPES } from 'features/appointment/services/constants';
import { ICalendarHeader, ICalendarItemData } from 'features/appointment/services/types/calendar';
import { maxBy, minBy } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { momentTimezone } from 'utils/time';
import Appointment from './Appointment';
import BlockHour from './BlockHour';
import BreakStaff from './BreakStaff';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type Range = { start: number; span: number };

const computePosition = (item: ICalendarItemData, range: Range) => {
  const start = momentTimezone(item.startTime).valueOf();
  const end = momentTimezone(item.endTime).valueOf();
  const top = clamp(((start - range.start) / range.span) * 100, 0, 100);
  const height = clamp(((end - start) / range.span) * 100, 0, 100 - top);
  return { top, height };
};

type Props = {
  blockItems: ICalendarItemData[];
  appointments: ICalendarItemData[];
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  v2?: boolean;
};

/**
 * Render cột có cả block/break LẪN appointment dưới dạng 2 track BÊN TRONG một cell duy nhất:
 *   - Track trái: block/break, mỗi item định vị tuyệt đối theo thời gian.
 *   - Track phải: từng appointment định vị tuyệt đối theo thời gian.
 *
 * Cách này giữ table luôn 1 cột/header (không colspan) nên không phá vỡ alignment,
 * và tránh việc block bị merge chung span với appointment rồi bị giấu mất.
 */
const SplitBlockColumn: React.FC<Props> = ({ blockItems, appointments, headerData, isMonthView, v2 }) => {
  // Khoảng thời gian của cell = extent của TẤT CẢ item trong cell (block + apt),
  // khớp với merged span mà hook tạo ra (rowStart = item sớm nhất, rowEnd = item muộn nhất).
  const range = useMemo<Range>(() => {
    const allItems = [...blockItems, ...appointments];
    const startItem = minBy(allItems, o => momentTimezone(o.startTime).valueOf());
    const endItem = maxBy(allItems, o => momentTimezone(o.endTime).valueOf());
    const start = momentTimezone(startItem?.startTime).valueOf();
    const end = momentTimezone(endItem?.endTime).valueOf();
    return { start, span: Math.max(end - start, 1) };
  }, [blockItems, appointments]);

  const blockSlots = useMemo(
    () => blockItems.map(item => ({ item, ...computePosition(item, range) })),
    [blockItems, range],
  );
  const aptSlots = useMemo(
    () => appointments.map(item => ({ item, ...computePosition(item, range) })),
    [appointments, range],
  );

  return (
    <SplitStyled>
      <div className="block-track">
        {blockSlots.map(({ item, top, height }) => (
          <div key={item.id} className="slot" style={{ top: `${top}%`, height: `${height}%` }}>
            {item.type === CALENDAR_ITEM_TYPES.BREAK_STAFF
              ? <BreakStaff data={[item]} isMonthView={isMonthView} headerData={headerData} />
              : <BlockHour data={[item]} isMonthView={isMonthView} headerData={headerData} />}
          </div>
        ))}
      </div>
      <div className="apt-track">
        {aptSlots.map(({ item, top, height }) => (
          <div key={item.id} className="slot" style={{ top: `${top}%`, height: `${height}%` }}>
            <Appointment data={item} headerData={headerData} isMonthView={isMonthView} v2={v2} />
          </div>
        ))}
      </div>
    </SplitStyled>
  );
};

export default React.memo(SplitBlockColumn);

const SplitStyled = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  gap: 2px;
  .block-track {
    flex: 0 0 50%;
    height: 100%;
    position: relative;
    zoom: 0.7;
  }
  .apt-track {
    flex: 1;
    height: 100%;
    position: relative;
    zoom: 0.7;
  }
  .slot {
    position: absolute;
    left: 0;
    right: 0;
    overflow: hidden;
  }
`;
